import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, getToken, qs } from '../api/client';
import { urlImpression } from '../api/config';
import { useAuth } from '../state/auth';
import { Badge, Carte, Champ, Squelette, Vide } from '../components/ui';
import { useMobile } from '../lib/ecran';
import { dateFr, montant, nomClient } from '../lib/format';
import type { Client, Contrat, Reservation, Vehicule } from '../api/types';

/**
 * L'archive des contrats.
 *
 * Elle manquait. `GET /contrats` existait depuis le debut, aucun ecran ne
 * l'affichait — et c'est le premier endroit ou le patron a cherche. Un contrat
 * signe est la piece qui compte : on doit pouvoir le retrouver par plaque, par
 * client ou par numero, et le reimprimer, des mois plus tard.
 */
const FILTRES = [
  { cle: 'actif', label: 'En cours', statuts: 'actif' },
  { cle: 'clos', label: 'Terminés', statuts: 'clos' },
  { cle: 'resilie', label: 'Résiliés', statuts: 'resilie' },
  { cle: 'tous', label: 'Tous', statuts: '' },
];

export default function Contrats() {
  const { peut } = useAuth();
  const mobile = useMobile();
  const [filtre, setFiltre] = useState('actif');
  const [recherche, setRecherche] = useState('');
  const [liste, setListe] = useState<Contrat[] | null>(null);

  const charger = useCallback(() => {
    const f = FILTRES.find((x) => x.cle === filtre);
    setListe(null);
    api.get<Contrat[]>(`/contrats${qs({ statut: f?.statuts || undefined })}`).then(setListe).catch(() => setListe([]));
  }, [filtre]);
  useEffect(charger, [charger]);

  const q = recherche.trim().toLowerCase();
  const visibles = (liste ?? []).filter((c) => {
    if (!q) return true;
    const v = c.vehicle as Vehicule;
    const cl = c.customer as Client;
    const r = c.reservation as Reservation;
    return [c.numero, r?.numero, v?.marque, v?.modele, v?.immatriculation, nomClient(cl)]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(q));
  });

  const idResa = (c: Contrat) =>
    (typeof c.reservation === 'string' ? c.reservation : c.reservation?._id);

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Contrats</h1>
          <p>Tous vos contrats signés · retrouvez-en un et réimprimez-le</p>
        </div>
      </div>

      <Carte pad={false} titre={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {FILTRES.map((f) => (
            <button key={f.cle} className={`btn btn-s ${filtre === f.cle ? 'btn-p' : ''}`}
                    onClick={() => setFiltre(f.cle)}>{f.label}</button>
          ))}
        </div>
      } action={
        <Champ label="">
          <input placeholder="N° de contrat, client, marque ou plaque…" value={recherche}
                 onChange={(e) => setRecherche(e.target.value)} />
        </Champ>
      }>
        {liste === null ? (
          <div className="carte-p"><Squelette lignes={5} /></div>
        ) : visibles.length === 0 ? (
          <div className="carte-p">
            <Vide titre={q ? 'Aucun contrat ne correspond' : 'Aucun contrat ici'}>
              {q
                ? <>Essayez avec la plaque, le nom du client, ou changez de filtre.</>
                : <>Un contrat est édité depuis le dossier d'une location, au moment du départ.
                   Changez de filtre pour voir les contrats terminés.</>}
            </Vide>
          </div>
        ) : mobile ? (
          <div className="cartes">
            {visibles.map((c) => {
              const v = c.vehicle as Vehicule;
              return (
                <div key={c._id}>
                <Link className="carte-ligne" to={`/reservations/${idResa(c)}`}>
                  <div className="cl-tete">
                    <span className="cl-titre">{c.numero}</span>
                    <Badge statut={c.statut} />
                  </div>
                  <div className="cl-sous">{nomClient(c.customer as Client)} · {v?.marque} {v?.modele}</div>
                  <div className="cl-sous">{dateFr(c.dateContrat)}</div>
                  <div className="cl-pied montant">
                    {c.soldeMinor > 0
                      ? <span style={{ color: 'var(--danger)' }}>Reste dû {montant(c.soldeMinor)}</span>
                      : <>Soldé · {montant(c.totalGeneralMinor)}</>}
                  </div>
                </Link>
                {/* Sur telephone la carte entiere ouvre le dossier : le lien
                    d'impression doit vivre a cote, pas dedans. */}
                <a className="btn btn-s" target="_blank" rel="noreferrer"
                   style={{ margin: '0 12px 12px' }}
                   href={urlImpression(`/contrats/${c._id}/imprimer`, getToken())}>Imprimer le contrat</a>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr>
                  <th>N° contrat</th><th>Date</th><th>Client</th><th>Véhicule</th>
                  <th className="droite">Total</th><th className="droite">Reste dû</th>
                  <th>Statut</th><th />
                </tr>
              </thead>
              <tbody>
                {visibles.map((c) => {
                  const v = c.vehicle as Vehicule;
                  return (
                    <tr key={c._id}>
                      <td><Link to={`/reservations/${idResa(c)}`}><b>{c.numero}</b></Link></td>
                      <td className="mini sourdine">{dateFr(c.dateContrat)}</td>
                      <td>{nomClient(c.customer as Client)}</td>
                      <td>{v?.marque} {v?.modele} <span className="mini sourdine">{v?.immatriculation}</span></td>
                      <td className="droite montant">{montant(c.totalGeneralMinor)}</td>
                      <td className="droite montant" style={c.soldeMinor > 0 ? { color: 'var(--danger)' } : undefined}>
                        {c.soldeMinor > 0 ? montant(c.soldeMinor) : '—'}
                      </td>
                      <td><Badge statut={c.statut} /></td>
                      <td className="droite">
                        {peut('voir.contrats') && (
                          <a className="btn btn-s" target="_blank" rel="noreferrer"
                             href={urlImpression(`/contrats/${c._id}/imprimer`, getToken())}>Imprimer</a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {visibles.length} contrat(s) affiché(s){q && ` sur ${(liste ?? []).length}`}.
        Un contrat imprimé est identique à l'original, même des mois après — il est inaltérable.
      </p>
    </>
  );
}
